import { motion } from 'framer-motion';
import { TrendingUp, DollarSign, Users, Shield, Clock, ArrowRight, BarChart3, CheckCircle, Zap } from 'lucide-react';

const InsuranceResults = () => {
  const caseStudies = [
    {
      company: "Emirates Health Insurance",
      sector: "Major Health Insurer",
      members: "500,000+",
      timeframe: "12 months",
      challenge: "Rising claims costs and fraudulent activities draining profitability",
      solution: "Implemented comprehensive HIERSTO platform with advanced fraud detection and predictive analytics",
      results: {
        costSavings: "AED 45M",
        fraudReduction: "85%",
        processingSpeed: "70%",
        memberSatisfaction: "92%"
      },
      roi: "340%",
      paybackPeriod: "8 months",
      testimonial: "HIERSTO transformed our operations. The fraud detection alone saved us millions, and our members love the faster service.",
      author: "Dr. Sarah Al-Mansouri, Chief Medical Officer",
      metrics: [
        { metric: "Claims Cost Reduction", value: "35%", icon: DollarSign },
        { metric: "Fraud Detection Rate", value: "98.7%", icon: Shield },
        { metric: "Processing Speed", value: "70% faster", icon: Clock },
        { metric: "Member Retention", value: "94%", icon: Users }
      ]
    },
    {
      company: "Gulf Medical Insurance",
      sector: "Regional Insurance Provider",
      members: "200,000+",
      timeframe: "8 months",
      challenge: "Manual processes causing delays and high operational costs",
      solution: "Deployed automated workflow and smart approval systems with member portal integration",
      results: {
        costSavings: "AED 18M",
        fraudReduction: "78%",
        processingSpeed: "65%",
        memberSatisfaction: "89%"
      },
      roi: "285%",
      paybackPeriod: "6 months",
      testimonial: "The automation capabilities exceeded our expectations. We're now processing claims in minutes instead of days.",
      author: "Ahmed Al-Rashid, Operations Director",
      metrics: [
        { metric: "Operational Efficiency", value: "80%", icon: Zap },
        { metric: "Cost Per Claim", value: "45% lower", icon: DollarSign },
        { metric: "Approval Time", value: "5 minutes", icon: Clock },
        { metric: "Staff Productivity", value: "3x higher", icon: Users }
      ]
    }
  ];

  const industryBenchmarks = [
    {
      category: "Cost Management",
      industry: "15-20%",
      hiersto: "35-45%",
      metric: "Annual cost reduction",
      improvement: "120% better"
    },
    {
      category: "Fraud Detection",
      industry: "70-80%",
      hiersto: "98.5%",
      metric: "Detection accuracy",
      improvement: "25% better"
    },
    {
      category: "Processing Speed",
      industry: "5-7 days",
      hiersto: "2-4 hours",
      metric: "Average claim processing",
      improvement: "90% faster"
    },
    {
      category: "Member Satisfaction",
      industry: "65-75%",
      hiersto: "90-95%",
      metric: "Satisfaction rating",
      improvement: "30% higher"
    }
  ];

  const roiCalculator = {
    title: "Average ROI Breakdown",
    components: [
      { item: "Fraud Detection Savings", amount: "AED 12M", percentage: 40 },
      { item: "Operational Efficiency", amount: "AED 8M", percentage: 27 },
      { item: "Claims Cost Reduction", amount: "AED 7M", percentage: 23 },
      { item: "Member Retention Value", amount: "AED 3M", percentage: 10 }
    ],
    total: "AED 30M",
    investment: "AED 9M",
    netROI: "AED 21M",
    roiPercentage: "233%"
  };

  const quickWins = [
    { timeframe: "Week 1", achievement: "Platform deployment and initial setup", impact: "System operational" },
    { timeframe: "Week 2", achievement: "Staff training and basic fraud detection active", impact: "First fraudulent claims blocked" },
    { timeframe: "Month 1", achievement: "Automated workflows live", impact: "50% faster processing" },
    { timeframe: "Month 2", achievement: "AI models calibrated to your data", impact: "Predictive accuracy improves" },
    { timeframe: "Month 3", achievement: "Full feature rollout complete", impact: "Maximum ROI achieved" }
  ];

  return (
    <section id="results" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-[#90E0EF]/20 border border-[#90E0EF]/30 rounded-full mb-6">
            <TrendingUp className="w-4 h-4 text-[#03045E] mr-2" />
            <span className="text-[#03045E] font-semibold text-sm">Proven Results & ROI</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-[#03045E] mb-6">
            <span className="text-[#0077B6]">Real Results</span> from Real Partners
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            See how insurance companies across the region are achieving remarkable cost savings, 
            operational improvements, and member satisfaction with HIERSTO.
          </p>
        </motion.div>

        {/* ROI Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="bg-gradient-to-r from-[#CAF0F8]/30 to-[#90E0EF]/30 rounded-3xl p-8 border border-[#90E0EF]/30">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              
              {/* Left: ROI Breakdown */}
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-6">
                  {roiCalculator.title}
                </h3>
                
                <div className="space-y-4 mb-6">
                  {roiCalculator.components.map((component, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-1">
                          <span className="text-sm text-gray-700">{component.item}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${component.percentage}%` }}
                            transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                            viewport={{ once: true }}
                            className="h-2 bg-[#0077B6] rounded-full"
                          />
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-lg font-bold text-[#03045E]">{component.amount}</div>
                        <div className="text-xs text-gray-500">{component.percentage}%</div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Total Savings</div>
                      <div className="text-2xl font-bold text-[#0077B6]">{roiCalculator.total}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Net ROI</div>
                      <div className="text-2xl font-bold text-green-600">{roiCalculator.roiPercentage}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Quick Wins Timeline */}
              <div>
                <h4 className="text-xl font-bold text-[#03045E] mb-6">Path to ROI: Quick Wins Timeline</h4>
                <div className="space-y-4">
                  {quickWins.map((win, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="bg-[#90E0EF] text-[#03045E] px-2 py-1 rounded-lg text-xs font-semibold flex-shrink-0">
                          {win.timeframe}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-[#03045E] mb-1">{win.achievement}</div>
                          <div className="text-xs text-gray-600">{win.impact}</div>
                        </div>
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Case Studies */}
        <div className="space-y-12 mb-16">
          {caseStudies.map((study, studyIndex) => (
            <motion.div
              key={studyIndex}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: studyIndex * 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl shadow-lg border border-gray-100 overflow-hidden"
            >
              <div className="grid lg:grid-cols-3 gap-0">
                
                {/* Left: Company Info */}
                <div className="bg-gradient-to-br from-[#0077B6] to-[#03045E] text-white p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold mb-2">{study.company}</h3>
                    <p className="text-[#CAF0F8] mb-1">{study.sector}</p>
                    <p className="text-[#CAF0F8] text-sm">{study.members} members</p>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-2">Challenge</h4>
                    <p className="text-[#CAF0F8] text-sm leading-relaxed">{study.challenge}</p>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-2">Solution</h4>
                    <p className="text-[#CAF0F8] text-sm leading-relaxed">{study.solution}</p>
                  </div>

                  <div className="bg-white/10 rounded-xl p-4">
                    <div className="grid grid-cols-2 gap-3 text-center">
                      <div>
                        <div className="text-2xl font-bold text-[#CAF0F8]">{study.roi}</div>
                        <div className="text-xs text-[#90E0EF]">ROI Achieved</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-[#CAF0F8]">{study.paybackPeriod}</div>
                        <div className="text-xs text-[#90E0EF]">Payback Period</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Middle: Metrics */}
                <div className="p-8">
                  <h4 className="text-xl font-bold text-[#03045E] mb-6">Key Results</h4>
                  <div className="space-y-4">
                    {study.metrics.map((metric, metricIndex) => (
                      <motion.div
                        key={metricIndex}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.5 + metricIndex * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-xl"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="bg-[#90E0EF]/20 p-2 rounded-lg">
                            <metric.icon className="w-4 h-4 text-[#0077B6]" />
                          </div>
                          <span className="text-sm text-gray-700">{metric.metric}</span>
                        </div>
                        <span className="text-lg font-bold text-[#0077B6]">{metric.value}</span>
                      </motion.div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-[#CAF0F8]/20 rounded-xl border border-[#90E0EF]/30">
                    <div className="text-center">
                      <div className="text-sm text-gray-600 mb-1">Total Savings</div>
                      <div className="text-3xl font-bold text-[#0077B6]">{study.results.costSavings}</div>
                      <div className="text-xs text-gray-500">in {study.timeframe}</div>
                    </div>
                  </div>
                </div>

                {/* Right: Testimonial */}
                <div className="bg-gray-50 p-8 flex flex-col justify-center">
                  <div className="mb-6">
                    <div className="text-4xl text-[#90E0EF] mb-3">"</div>
                    <p className="text-gray-700 leading-relaxed italic mb-4">{study.testimonial}</p>
                    <div className="text-sm">
                      <div className="font-semibold text-[#03045E]">{study.author}</div>
                      <div className="text-gray-600">{study.company}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: 1 + i * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <div className="w-4 h-4 bg-yellow-400 rounded-full"></div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Industry Benchmarks */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
              How We Compare to Industry Standards
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              See how HIERSTO partners consistently outperform industry benchmarks across all key metrics.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industryBenchmarks.map((benchmark, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center"
              >
                <h4 className="text-lg font-bold text-[#03045E] mb-4">{benchmark.category}</h4>
                
                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Industry Average</div>
                    <div className="text-lg font-semibold text-gray-500">{benchmark.industry}</div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="text-sm text-gray-600 mb-1">HIERSTO Partners</div>
                    <div className="text-2xl font-bold text-[#0077B6]">{benchmark.hiersto}</div>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-green-50 rounded-xl">
                  <div className="text-xs text-gray-600 mb-1">{benchmark.metric}</div>
                  <div className="text-sm font-bold text-green-600">{benchmark.improvement}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-[#90E0EF]/30">
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
              Ready to achieve similar results for your organization?
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Join the growing number of insurance companies transforming their operations and 
              achieving exceptional ROI with HIERSTO.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#03045E] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#03045E]/90 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-2"
              >
                <span>Start Your Transformation</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-transparent border-2 border-[#0077B6] text-[#0077B6] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#0077B6] hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <BarChart3 className="w-5 h-5" />
                <span>Calculate Your ROI</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InsuranceResults;
